import * as React from 'react';

// Placeholder
import {
  PageGeneric as PageGenericPlaceholder,
  PageUser as PageUserPlaceholder,
} from '@shopee-user/placeholder-ui';

// Routes utils
import { PageName, ROUTES, getPathRelativeToBasePath } from '@shopee-user/url';
import { generateAsyncRouteComponent } from '@shopee-user/generate-async-route-component';

// I18n constants & utils
import {
  USER_ACCOUNT_COLLECTION,
  USER_EMAIL_COLLECTION,
  USER_PHONE_COLLECTION,
  USER_404_COLLECTION,
} from '@shopee-user/transify-collections';

// Feature toggle
import { isFeatureEnabled } from '@shopee-user/business-customization-logic';
import { Feature } from '@shopee-user/feature-toggle';

// Elements
const PageUserPassword = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageUserPassword" */ '@tob/pages/user-password'
    ),
  Placeholder: PageUserPlaceholder,
  i18nCollections: [USER_ACCOUNT_COLLECTION],
});

const PageUserAccount = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageUserAccount" */ '@tob/pages/user-account-setting'
    ),
  Placeholder: PageGenericPlaceholder,
  i18nCollections: [USER_ACCOUNT_COLLECTION],
});

const PageUserEmail = generateAsyncRouteComponent({
  loader: () =>
    import(/* webpackChunkName: "PageUserEmail" */ '@tob/pages/user-email'),
  Placeholder: PageUserPlaceholder,
  i18nCollections: [USER_EMAIL_COLLECTION],
});

const PageUserPhone = generateAsyncRouteComponent({
  loader: () =>
    import(/* webpackChunkName: "PageUserPhone" */ '@tob/pages/user-phone'),
  Placeholder: PageUserPlaceholder,
  i18nCollections: [USER_PHONE_COLLECTION],
});

const PageUserAccountLinkage = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageUserAccountLinkage" */ '@tob/pages/user-account-linkage'
    ),
  Placeholder: PageUserPlaceholder,
  i18nCollections: [USER_ACCOUNT_COLLECTION],
});

const Page404 = generateAsyncRouteComponent({
  loader: () => import(/* webpackChunkName: "Page404" */ '@tob/pages/page-404'),
  Placeholder: PageGenericPlaceholder,
  i18nCollections: [USER_404_COLLECTION],
});

const FINAL_ROUTES: {
  path: string;
  name: string;
  featureToggle?: boolean;
  element: React.ReactNode;
}[] = [
  {
    path: ROUTES.USER_PASSWORD,
    name: PageName.PageUserPassword,
    featureToggle:
      isFeatureEnabled(Feature.AdminManagePassword) ||
      isFeatureEnabled(Feature.UserManagePassword) ||
      isFeatureEnabled(Feature.UserPasswordEntrance),
    element: <PageUserPassword />,
  },
  {
    path: ROUTES.USER_ACCOUNT,
    name: PageName.PageUserAccount,
    featureToggle: isFeatureEnabled(Feature.UserAccountEntrance),
    element: <PageUserAccount />,
  },
  {
    path: ROUTES.USER_EMAIL,
    name: PageName.PageUserEmail,
    featureToggle: isFeatureEnabled(Feature.UserEmailEntrance),
    element: <PageUserEmail />,
  },
  {
    path: ROUTES.USER_PHONE,
    name: PageName.PageUserPhone,
    featureToggle: isFeatureEnabled(Feature.UserPhoneEntrance),
    element: <PageUserPhone />,
  },
  {
    path: ROUTES.USER_ACCOUNT_LINKAGE,
    name: PageName.PageUserAccountLinkage,
    // Linkage feature also relies on Thirdparty integration toggles
    // But now we only check UserAccountLinkage toggle here
    // Maybe we have to modify the toggle checking logic in the future if we have more 3p integration cases
    featureToggle: isFeatureEnabled(Feature.UserAccountLinkage),
    element: <PageUserAccountLinkage />,
  },
  {
    path: '*',
    name: PageName.Page404,
    element: <Page404 />,
  },
];

export default FINAL_ROUTES.filter(
  routes => routes.featureToggle !== false
).map(route => ({
  ...route,
  path: getPathRelativeToBasePath({ route: route.path, basePath: ROUTES.USER }),
}));
